import {
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta
} from '@reduxjs/toolkit/query';
import { logout } from '../auth';
import { jwtDecode } from 'jwt-decode';
import authApi from '../auth/authApi';

export const CACHING_TIME = 5 * 60 * 1000;

export type BaseQuery = {
  baseUrl: string;
  credentials?: RequestCredentials;
  setHeaders?: (headers: Headers, endpoint: { endpoint: string }) => Headers;
};

// Define custom baseQuery function with configuration
export const customBaseQuery = ({
  baseUrl,
  credentials,
  setHeaders
}: BaseQuery) => {
  return fetchBaseQuery({
    baseUrl,
    credentials: credentials || 'same-origin',
    prepareHeaders(headers, { endpoint }) {
      return setHeaders ? setHeaders(headers, { endpoint }) : headers;
    }
  });
};

export const BaseQueryWithReAuth = ({
  baseUrl,
  credentials,
  setHeaders
}: BaseQuery): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  {},
  FetchBaseQueryMeta
> => {
  if (typeof baseUrl !== 'string') {
    throw new Error('Invalid type for baseUrl: expected a string.');
  }
  return async (args, api, extraOptions) => {
    const baseQuery = fetchBaseQuery({
      baseUrl,
      credentials: credentials || 'same-origin', // Default to 'same-origin' if not provided
      prepareHeaders(headers, { endpoint }) {
        if (setHeaders) {
          setHeaders(headers, { endpoint });
        }
        return headers; // Return the modified headers
      }
    });
    let result = await baseQuery(args, api, extraOptions);
    if (result.error) {
      const { status } = result.error;
      if (status === 401 && localStorage.getItem('accessToken')) {
        const refreshToken = localStorage.getItem('refreshToken') || '';
        const token = localStorage.getItem('accessToken');

        if (!token || token === 'undefined') {
          api.dispatch(logout());
          return result;
        }

        try {
          const decodedToken = jwtDecode(token);
          const currentTime = Date.now() / 1000;
          const isExpired = decodedToken.exp && decodedToken.exp < currentTime;
          if (isExpired) {
            const { data, error } = await api
              .dispatch(
                authApi.endpoints.signInUser.initiate({
                  refreshToken,
                  authFlow: 'REFRESH_TOKEN_AUTH'
                })
              )
              .unwrap();
            if (error) {
              api.dispatch(logout());
            } else if (data) {
              const { accessToken, idToken } = data;
              if (accessToken && idToken) {
                localStorage.setItem('accessToken', accessToken);
                localStorage.setItem('idToken', idToken);

                // Retry the original request with updated tokens
                result = await baseQuery(args, api, extraOptions);
              } else {
                api.dispatch(logout());
              }
            }
          } else {
            api.dispatch(logout());
          }
        } catch (error) {
          console.error('Error decoding token:', error);
          api.dispatch(logout());
        }
      } else if (status === 401) {
        api.dispatch(authApi.endpoints.signOutUser.initiate(''));
        api.dispatch(logout());
        localStorage.clear();
      }
    }
    return result;
  };
};

export const HES_TAG_TYPES = [
  'CommandList',
  'batchExecutionHistory',
  'DeviceInfo',
  'ConfigureCommand'
];
export const VEE_TAG_TYPES = [
  'HeadGroups',
  'HeadGroupDetails',
  'RuleGroups',
  'RuleGroupDetails',
  'Rules',
  'EstimationRules'
];
export const AUTH_TAG_TYPES = [];

export const RF_FW_UPGRADE_TAG_TYPES = [];

export const NFMS_TAG_TYPES = [];

export const PREPAID_ENGINE_TAG_TYPES = [];

export function buildQueryParam(paramName: string, items: string[]): string {
  if (!items.length) return '';

  return (
    '?' +
    items
      .map(
        (value) =>
          `${encodeURIComponent(paramName)}=${encodeURIComponent(value)}`
      )
      .join('&')
  );
}
